import React, { useState } from "react";
import { Modal, DatePicker, Checkbox, Segmented, Button, message } from "antd";
import { CloseOutlined, ExportOutlined } from "@ant-design/icons";
import {
  useGetDashboardStatsQuery,
  useGetOrganizationFormsQuery,
  useGetCampaignsQuery,
  useGetInterpretersQuery,
  useGetOrganizationsQuery,
} from "../../../store/api/apiSlice";
import moment from "moment";
import { getCampaignStatusFromForms } from "../../../utils/campaigns";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

applyPlugin(jsPDF);
import ExportReportModal from "../ExportReportModal";
import { useSelector } from "react-redux";

const ExportModalOrganization = ({ visible, onClose }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reportTypes, setReportTypes] = useState([]);
  const [fileFormat, setFileFormat] = useState("PDF");
  const [exporting, setExporting] = useState(false);
  const { user, token, role } = useSelector((state) => state.auth);

  const [viewType, setViewType] = useState("Forms");
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Query data with optional date range
  const dateParams =
    startDate && endDate
      ? {
          start_date: startDate.unix(),
          end_date: endDate.unix(),
        }
      : {};

  const { data: formsData } = useGetOrganizationFormsQuery(dateParams);
  const { data: campaignsData } = useGetCampaignsQuery(dateParams);
  const { data: interpretersData } = useGetInterpretersQuery(dateParams);
  const { data: organizationsData } = useGetOrganizationsQuery(dateParams);
  const handleStartDateChange = (date) => setStartDate(date);
  const handleEndDateChange = (date) => setEndDate(date);

  const handleReportTypeChange = (checkedValue) => {
    if (reportTypes.includes(checkedValue)) {
      setReportTypes(reportTypes.filter((type) => type !== checkedValue));
    } else {
      setReportTypes([...reportTypes, checkedValue]);
    }
  };

  // Filter forms based on selected campaign
  const getFilteredForms = () => {
    if (!formsData?.data) return [];

    // If no campaign is selected, show all forms
    if (!selectedCampaign) {
      return formsData.data;
    }

    // Find the selected campaign
    const campaign = campaignsData?.data?.find(
      (c) => c._id === selectedCampaign,
    );

    // If campaign not found or has no forms, return empty array
    if (!campaign || !campaign.forms || campaign.forms.length === 0) {
      return [];
    }

    // Extract form IDs from the campaign
    const campaignFormIds = campaign.forms.map((form) =>
      typeof form === "object" ? form._id : form,
    );

    // Filter forms by campaign form IDs
    return formsData.data.filter((form) => campaignFormIds.includes(form._id));
  };

  const getSelectedData = () => {
    let exportData = {};

    // Define table headers for each report type
    const tableHeaders = {
      forms: [
        "Title",
        "Organisations",
        "Questions",
        "Responses",
        "Status",
        "Created Date",
      ],
      campaigns: [
        "Title",
        "Organisations",
        "Forms",
        "Status",
      ],
      interpreters: [
        "Name",
        "Email",
        "Completed Assignments",
        "Total Assignments",
        "Status",
        "Joined Date",
      ],
      organizations: [
        "Name",
        "Location",
        "Campaigns",
        "Forms",
        "Users",
        "Status",
      ],
    };

    // Get filtered forms based on selected campaign
    const filteredForms = getFilteredForms();

    if (reportTypes.includes("formReport") || reportTypes.length === 0) {
      exportData.forms = {
        headers: tableHeaders.forms,
        data:
          filteredForms.map((form) => [
            form.title,
            form.organization?.name || "N/A",
            form.questions?.length || 0,
            form.responses?.length || 0,
            form.status,
            moment(form.created_at).format("YYYY-MM-DD"),
          ]) || [],
      };
    }

    if (reportTypes.includes("campaignReport") || reportTypes.length === 0) {
      exportData.campaigns = {
        headers: tableHeaders.campaigns,
        data:
          campaignsData?.data?.map((campaign) => [
            campaign.title,
            campaign.organizations?.map((org) => org.name).join(", "),
            campaign.forms?.length || 0,
            getCampaignStatusFromForms(campaign),
          ]) || [],
      };
    }

    if (reportTypes.includes("interpreterReport") || reportTypes.length === 0) {
      exportData.interpreters = {
        headers: tableHeaders.interpreters,
        data:
          interpretersData?.data?.map((interpreter) => [
            interpreter.user?.full_name,
            interpreter.user?.email,
            interpreter.completed_assignments || 0,
            interpreter.total_assignments || 0,
            interpreter.status,
            moment(interpreter.created_at).format("YYYY-MM-DD"),
          ]) || [],
      };
    }

    if (
      reportTypes.includes("organizationReport") ||
      reportTypes.length === 0
    ) {
      exportData.organizations = {
        headers: tableHeaders.organizations,
        data:
          organizationsData?.data?.map((org) => [
            org.name,
            org.location?.name || "N/A",
            org.campaigns_count || 0,
            org.forms_count || 0,
            org.users_count || 0,
            org.status,
          ]) || [],
      };
    }

    return exportData;
  };

  const exportToPDF = (data) => {
    const doc = new jsPDF();
    const dateRange =
      startDate && endDate
        ? `Date Range: ${startDate.format("YYYY-MM-DD")} to ${endDate.format(
            "YYYY-MM-DD",
          )}`
        : "All Time Data";

    // Add title and date range
    doc.setFontSize(16);
    doc.text("Analytics Report", 14, 15);
    doc.setFontSize(10);
    doc.text(dateRange, 14, 25);

    let yPosition = 35;

    Object.entries(data).forEach(([section, { headers, data }]) => {
      // Add section title
      doc.setFontSize(12);
      doc.text(
        `${section.charAt(0).toUpperCase() + section.slice(1)} Report`,
        14,
        yPosition,
      );

      // Add table
      doc.autoTable({
        head: [headers],
        body: data,
        startY: yPosition + 5,
        headStyles: {
          fillColor: [24, 24, 27],
          textColor: [255, 255, 255],
          fontSize: 10,
          halign: "center",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        theme: "grid",
        margin: { left: 14, right: 14 },
        didDrawPage: function (data) {
          // Update yPosition for next section
          yPosition = data.cursor.y + 15;

          // Add new page if there's not enough space
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
        },
      });
    });

    doc.save(`analytics_report_${moment().format("YYYY-MM-DD")}.pdf`);
  };

  const exportToExcel = (data) => {
    const wb = XLSX.utils.book_new();

    Object.entries(data).forEach(([section, { headers, data }]) => {
      const wsData = [headers, ...data];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, section);
    });

    XLSX.writeFile(wb, `report_${moment().format("YYYY-MM-DD")}.xlsx`);
  };

  const exportToCSV = (data) => {
    Object.entries(data).forEach(([section, { headers, data }]) => {
      const csvContent = [
        headers.join(","),
        ...data.map((row) =>
          row
            .map((cell) =>
              typeof cell === "string" && cell.includes(",")
                ? `"${cell}"`
                : cell,
            )
            .join(","),
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${section}_report_${moment().format("YYYY-MM-DD")}.csv`;
      link.click();
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const data = getSelectedData();

      switch (fileFormat) {
        case "PDF":
          exportToPDF(data);
          break;
        case "Excel":
          exportToExcel(data);
          break;
        case "CSV":
          exportToCSV(data);
          break;
      }

      message.success("Report exported successfully");
      onClose();
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      closeIcon={<CloseOutlined className="text-gray-400" />}
      className="custom-modal"
    >
      <div className="flex justify-between flex-wrap">
        <h2>Export Analytics Report</h2>
        {/* <div className="flex bg-secondary rounded-xl h-10 p-1.5 mr-6">
          <button
            className={`p-2 border-0 rounded-lg ${
              viewType === "Analytics" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("Analytics")}
          >
            Analytics
          </button>
          <button
            className={`p-2 border-0 rounded-lg ${
              viewType === "Forms" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("Forms")}
          >
            Forms
          </button>
        </div> */}
      </div>
      {viewType === "Analytics" ? (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <p className="text-sm font-medium mb-2">Start Date (Optional)</p>
              <DatePicker
                placeholder="Select start date"
                className="w-full rounded-2xl h-12"
                onChange={handleStartDateChange}
                value={startDate}
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-2 ">End Date (Optional)</p>
              <DatePicker
                placeholder="Select end date"
                className="w-full rounded-2xl h-12"
                onChange={handleEndDateChange}
                value={endDate}
              />
            </div>
          </div>

          <div className="w-[96%]">
            <p className="text-sm font-medium mb-3">Select Report Type</p>
            <div className="bg-gray-50 p-4 rounded-2xl w-full">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Checkbox onChange={() => handleReportTypeChange("formReport")}>
                  Form Report
                </Checkbox>
                <Checkbox
                  onChange={() => handleReportTypeChange("campaignReport")}
                >
                  Campaign Report
                </Checkbox>
                <Checkbox
                  onChange={() => handleReportTypeChange("interpreterReport")}
                >
                  Interpreter Report
                </Checkbox>
                <Checkbox
                  onChange={() => handleReportTypeChange("organizationReport")}
                >
                  Organisation Report
                </Checkbox>
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-3">Select File Format</p>
            <Segmented
              value={fileFormat}
              onChange={setFileFormat}
              options={[
                { label: "PDF", value: "PDF" },
                { label: "CSV", value: "CSV" },
                { label: "Excel", value: "Excel" },
              ]}
              className="w-full bg-gray-50 p-1 rounded-2xl"
              block
            />
          </div>

          <Button
            type="primary"
            onClick={handleExport}
            loading={exporting}
            className="w-full h-12 rounded-2xl bg-[#18181B] hover:bg-[#18181B]/90 flex items-center justify-center gap-2"
            icon={<ExportOutlined />}
          >
            {exporting ? "Exporting..." : "Export Report"}
          </Button>
        </div>
      ) : (
        <ExportReportModal
          isNotModal={true}
          selectedCampaignProp={selectedCampaign}
          setSelectedCampaignProp={setSelectedCampaign}
        />
      )}
    </Modal>
  );
  a;
};

export default ExportModalOrganization;
